import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Boxes } from 'lucide-react';
import { Container, PageHeader, Section } from '@vnetwork/ui/layout';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  Skeleton,
} from '@vnetwork/ui/primitives';
import { DataTable, Stat, StatGroup, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { useUsageOverview, useUsageSeries, type BucketUsage } from '@/api/s3';
import { formatNum } from '@/pages/services/_shared/analytics-format';
import { formatGiB } from '@/pages/services/oss/storage-format';
import { S3ConsoleBreadcrumbs } from '../S3ConsoleBreadcrumbs';
import { LinkButton } from '../s3-parts';
import { StorageUtilizationChart } from './StorageUtilizationChart';

/** Account-wide storage overview — accurate stat cards, utilization chart, and
 * (when available) a per-bucket usage table. */
export function Overview({ serviceId }: { serviceId: string }) {
  const navigate = useNavigate();
  const { data, isLoading } = useUsageOverview(serviceId);
  const { data: series = [], isLoading: seriesLoading } = useUsageSeries(serviceId);

  const rows = useMemo<BucketUsage[]>(
    () => [...(data?.by_bucket ?? [])].sort((a, b) => b.size - a.size),
    [data],
  );

  // Show the per-bucket table only when the overview actually carries per-bucket
  // rows — the accurate Wasabi path may omit them, and an always-empty table reads
  // as broken. During the initial load we still want the skeleton, hence `||`.
  const hasBuckets = isLoading || rows.length > 0;

  const columns: ColumnDef<BucketUsage, unknown>[] = [
    {
      id: 'name',
      header: 'Bucket',
      cell: ({ row }) => (
        <LinkButton
          onClick={() =>
            navigate(`/services/oss/${serviceId}/buckets/${row.original.name}/objects`)
          }
        >
          {row.original.name}
        </LinkButton>
      ),
    },
    {
      id: 'objects',
      header: 'Objects',
      size: 140,
      cell: ({ row }) => formatNum(row.original.objects),
    },
    {
      id: 'size',
      header: 'Size',
      size: 140,
      cell: ({ row }) => formatGiB(row.original.size),
    },
  ];

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={<S3ConsoleBreadcrumbs />}
        title="Overview"
        description="Buckets, total storage and object counts across this service."
      />

      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <Section
        title="Storage usage"
        description={
          data?.as_of_date
            ? `Accurate roll-up as of ${data.as_of_date}.`
            : 'Live roll-up across all buckets.'
        }
        style={{ marginBottom: 0 }}
      >
        {/* Prefer the accurate Wasabi figures (`active_storage_bytes` / `objects`);
            fall back to the legacy walk estimates when the accurate path is absent.
            `*_change` fields are an upstream flag (not a % delta) — deliberately
            not rendered to avoid a misleading trend indicator. */}
        <StatGroup columns={4}>
            <Stat
              label="Active storage"
              value={
                isLoading ? (
                  <Skeleton style={{ height: 28, width: 80 }} />
                ) : (
                  formatGiB(data?.active_storage_bytes ?? data?.total_size ?? 0)
                )
              }
            />
            <Stat
              label="Deleted storage"
              value={
                isLoading ? (
                  <Skeleton style={{ height: 28, width: 80 }} />
                ) : (
                  formatGiB(data?.deleted_storage_bytes ?? 0)
                )
              }
            />
            <Stat
              label="Objects"
              value={
                isLoading ? (
                  <Skeleton style={{ height: 28, width: 70 }} />
                ) : (
                  formatNum(data?.objects ?? data?.total_objects ?? 0)
                )
              }
            />
            <Stat
              label="Buckets"
              value={
                isLoading ? (
                  <Skeleton style={{ height: 28, width: 60 }} />
                ) : (
                  formatNum(data?.buckets ?? 0)
                )
              }
            />
        </StatGroup>
      </Section>

      <StorageUtilizationChart series={series} isLoading={seriesLoading} />

      {hasBuckets ? (
      <Card flat>
        <CardHeader>
          <CardTitle>Usage by bucket</CardTitle>
          <CardDescription>Per-bucket size and object count.</CardDescription>
        </CardHeader>
        <CardBody>
          {isLoading ? (
            <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} variant="rect" height={40} />
              ))}
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={rows}
              getRowId={(r) => r.name}
              emptyState={
                <EmptyState
                  icon={<Boxes size={20} />}
                  title="No buckets yet"
                  description="Create a bucket to start storing objects."
                />
              }
            />
          )}
        </CardBody>
      </Card>
      ) : null}
      </div>
    </Container>
  );
}
